import type { RobotView } from '../api.js';

/**
 * The one-line figures beside a robot's name: flag, damage, archive, and
 * whatever else is true of it.
 *
 * Shared by the live game's roster and the replay's, which show the same
 * robot in the same idiom and must not drift apart. What separates them
 * is what they can honestly fill in: a live view knows everything, while
 * a replay knows only what the frame recorded. EVERY FIELD BELOW IS
 * OMITTED WHEN ABSENT, which is what lets the replay pass the subset it
 * has rather than a line of placeholders.
 */
export function RobotStats({ robot }: {
  robot: Pick<RobotView,
    'damage' | 'destroyed' | 'lastTouchedFlag' | 'archiveMarker' | 'lives'
    | 'deaths' | 'virtual' | 'poweredDown'
    | 'announcedPowerDownNextTurn'>
  & {
    /** Only the count and the names are drawn, so a
     * replay can hand over the shorter thing its frame recorded rather
     * than a full option view. */
    options?: { id: string; name: string }[];
  };
}) {
  return (
    /* Red for a robot that is switched off or is about to be, and the
       whole line rather than the words that say so: what that changes is
       every other figure on it. A powered-down robot is not going to move
       or shoot, and one announced for next turn is about to discard the
       damage shown beside its name. */
    <span
      className={`mono ${
        robot.poweredDown || robot.announcedPowerDownNextTurn
          ? 'power-off' : 'muted'
      }`}
    >
      flag <span className="num">{robot.lastTouchedFlag ?? 0}</span>
      {' / '}
      <span className="num">{robot.damage}</span> dmg
      {robot.archiveMarker && (
        <> {' / '}archive <span className="num">
          {robot.archiveMarker.x},{robot.archiveMarker.y}
        </span></>
      )}
      {robot.lives !== undefined
        ? <> {' / '}<span className="num">{robot.lives}</span> lives</>
        : robot.deaths
          ? <> {' / '}<span className="num">{robot.deaths}</span> deaths</>
          : null}
      {/* Named, not just counted. Which Options a robot holds is public
          and is most of what makes it dangerous, and a count says none of
          that -- least of all in a replay, where there is no tray to open
          and no live robot to ask. */}
      {robot.options && robot.options.length > 0 && (
        <> {' / '}<span className="num">{robot.options.length}</span>
          {robot.options.length === 1 ? ' option' : ' options'}
          {' ('}{robot.options.map((o) => o.name).join(', ')}{')'}
        </>
      )}
      {robot.virtual && ' / virtual'}
      {robot.destroyed && ' / out'}
      {robot.poweredDown && ' / powered down'}
      {/* An announcement, so it is public -- and the server only sends it
          once programming has closed. Separate from the line above rather
          than folded into it: a robot already down that has chosen to
          STAY down is both. */}
      {robot.announcedPowerDownNextTurn && ' / down next turn'}
    </span>
  );
}
